import { Injectable, Type } from '@angular/core';
import { MListData, PassengerType, SupportInformationData, SupportInformationParts } from '@common/interfaces';
import { SpecialAssistanceRequestData } from '@common/interfaces/mybooking/modals/passenger-information-input/special-assistance-request-modal.interface';
import { SupportModalBlockComponent } from '@lib/components/support-class';
import { ModalService } from '@lib/services';
import { BehaviorSubject } from 'rxjs';
import { GetOrderResponseData, GetOrderResponseDataAir, Traveler } from 'src/sdk-servicing';
import { PassengerInformationInputModalCommonService } from './passenger-information-input-modal.common.service';
import { SpecialAssistanceRequestModalService } from './special-assistance-request-modal.service';
import { isEmptyArray, isEmptyObject } from '@common/helpers';

@Injectable()
export class SupportInformationService {
  runInit$: BehaviorSubject<number> = new BehaviorSubject(0);

  constructor(
    private _modalService: ModalService,
    private _asrModalService: SpecialAssistanceRequestModalService,
    private _s04CommonSvc: PassengerInformationInputModalCommonService
  ) {}

  /**
   * サポート情報 初期値の作成
   * @param support FIXME: travelerSupports trong interface ở sdk đang là object. chờ version sdk mới
   * @returns サポート情報 初期値
   */
  createSupportInformationData(support: any): SupportInformationData {
    const pd006: Array<MListData> = this._s04CommonSvc.masterData.listDataAll.filter(
      (data) => data.data_code === 'PD_006' && data.lang === this._s04CommonSvc.lang
    );
    const pd960: Array<MListData> = this._s04CommonSvc.masterData.listDataAll.filter(
      (data) => data.data_code === 'PD_960' && data.lang === this._s04CommonSvc.lang
    );
    const walking = support.walking;
    const walkLevel = walking?.degreeSpecialServiceRequest?.[0]?.code ?? '';
    const pregnant = support.pregnant;
    const walkDisplay: string[] = [];
    walking?.degreeSpecialServiceRequest?.forEach((d: any) => {
      if (d.code) {
        walkDisplay.push(pd006?.find((pd) => pd?.value === d.code)?.display_content ?? '');
      }
    });
    let wheelchairData: any = [];
    if (!isEmptyObject(walking?.wheelChair)) {
      walking?.wheelChair?.forEach((d: any) => {
        if (d) {
          wheelchairData.push(this.createWheelchairData(d, pd960 ?? []));
        }
      });
    } else {
      wheelchairData = [
        {
          isNeededWheelchair: false,
          isNeededWheelchairDisplayValue: '',
          isWheelchairFoldable: false,
          isWheelchairFoldableDisplayValue: '',
          wheelchairType: '',
          wheelchairTypeDisplayValue: '',
          wheelchairBatteryType: '',
          wheelchairBatteryTypeDisplayValue: '',
          wheelchairDepth: 0,
          wheelchairWidth: 0,
          wheelchairHeight: 0,
          wheelchairWeight: 0,
        },
      ];
    }

    return {
      isSupportRegistered: support.isSupportRegistered ?? false,
      walk: walkLevel !== '',
      blind: support.blind?.isRequested ?? false,
      deaf: support.deaf?.isRequested ?? false,
      walkLevel: walkLevel,
      walkLevelDisplayValue: walkDisplay.join('<br>'),
      wheelchairData: wheelchairData,
      ...this.createPregnantData(pregnant),
      isError: false,
    };
  }

  /**
   * 指定されたパラメータに基づいて車椅子データ オブジェクトを作成します。
   * @param wheelChair FIXME: travelerSupports trong interface ở sdk đang là object. chờ version sdk mới
   * @param pd960 リストデータPD_960 バッテリータイプの表示名称
   * @returns さまざまなプロパティを持つ車椅子データ オブジェクト。
   */
  createWheelchairData(wheelChair: any, pd960: Array<MListData>) {
    const isNeededWheelchair = wheelChair?.isNeeded;
    const isWheelchairFoldable = wheelChair?.isFoldable;
    const wheelchairType = wheelChair?.type ?? '';
    const wheelchairBatteryType = this._s04CommonSvc.getWheelchairBatteryDisplayValue(wheelChair?.batteryType ?? '');

    return {
      isNeededWheelchair: isNeededWheelchair,
      isNeededWheelchairDisplayValue: this._s04CommonSvc.getBringWheelchairDisplayValue(isNeededWheelchair),
      isWheelchairFoldable: isWheelchairFoldable,
      isWheelchairFoldableDisplayValue: this._s04CommonSvc.getWheelchairFoldableDisplayValue(isWheelchairFoldable),
      wheelchairType: wheelchairType,
      wheelchairTypeDisplayValue: this._s04CommonSvc.getWheelchairTypeDisplayValue(wheelchairType),
      wheelchairBatteryType: wheelchairBatteryType,
      wheelchairBatteryTypeDisplayValue: pd960.find((pd) => pd.value === wheelchairBatteryType)?.display_content ?? '',
      wheelchairDepth: wheelChair?.depth ?? 0,
      wheelchairWidth: wheelChair?.width ?? 0,
      wheelchairHeight: wheelChair?.height ?? 0,
      wheelchairWeight: wheelChair?.weight ?? 0,
    };
  }

  /**
   * 指定されたパラメータに基づいて妊娠中のデータ オブジェクトを作成します。
   * @param pregnant FIXME: travelerSupports trong interface ở sdk đang là object. chờ version sdk mới
   * @returns さまざまなプロパティを持つ妊娠中のデータ オブジェクト。
   */
  createPregnantData(pregnant: any) {
    return {
      isPregnant: pregnant?.isPregnant ?? false,
      pregnantDoctorName: pregnant?.doctor?.[0]?.name ?? '',
      pregnantDoctorCountryPhoneExtension: pregnant?.doctor?.[0]?.phone?.countryPhoneExtension ?? '',
      pregnantDoctorPhoneNumber: pregnant?.doctor?.[0]?.phone?.number ?? '',
      pregnantDoctorEncryptedPhoneNumber: pregnant?.doctor?.[0]?.phone?.encryptedNumber ?? '',
      pregnantDueDate: this._s04CommonSvc.getDate(pregnant?.dueDate?.[0]),
      pregnantCondition: pregnant?.condition?.[0] ?? '',
    };
  }

  createSupportInformationParts(traveler: Traveler, pnr?: GetOrderResponseData): SupportInformationParts {
    const support = (pnr?.travelerSupports ?? ({} as any))?.[traveler.id ?? ''] ?? {};

    // 歩行の程度情報編集可否
    const isWalkLevelEditable = !Boolean((support?.walking?.degreeSpecialServiceRequest ?? []).length >= 2);
    // 車いす情報編集可否
    const isWheelchairEditable = !Boolean((support?.walking?.wheelChair ?? []).length >= 2);
    // 妊婦情報編集可否
    const isPregnantEditable = !Boolean(
      (support?.pregnant?.doctor ?? []).length >= 2 ||
        (support?.pregnant?.condition ?? []).length >= 2 ||
        (support?.pregnant?.dueDate ?? []).length >= 2
    );
    const isSupportInformationEditable = isWalkLevelEditable && isWheelchairEditable && isPregnantEditable;

    const isDomesticFlight: boolean = pnr?.air?.tripType === GetOrderResponseDataAir.TripTypeEnum.Domestic;
    const isInfantExisting: boolean =
      pnr?.travelers?.some(
        (traveler) =>
          traveler?.passengerTypeCode === PassengerType.INF || traveler?.passengerTypeCode === PassengerType.INS
      ) ?? false;

    return {
      isDisplay: true,
      registrationClass: '',
      registrationLabel: '',
      isDomesticFlight: isDomesticFlight,
      isInfantExisting: isInfantExisting,
      isSupportInformationEditable: isSupportInformationEditable,
    };
  }

  handleClickEditSupportInformation(
    data: SupportInformationData,
    parts: SupportInformationParts,
    component: Type<SupportModalBlockComponent>,
    submitEvent: (value: SupportInformationData) => void,
    isDisableDegreeOfWaling: boolean
  ) {
    this._asrModalService.specialAssistanceRequestData = {
      isSupportRegistered: data.isSupportRegistered,
      specialAssistance: {
        ...this.getSpecialAssistanceData(data),
        isDisableDegreeOfWaling,
      },
      walkingAbility: {
        code: data.walkLevel ?? '',
      },
      wheelchairs: {
        willBeCheckedIn: data.wheelchairData?.[0]?.isNeededWheelchair,
      },
      foldingType: this.getFoldingTypeData(data),
      wheelchairType: {
        type: data.wheelchairData?.[0]?.wheelchairType ?? '',
        batteryType: data.wheelchairData?.[0]?.wheelchairBatteryType ?? '',
      },
      pregnant: this.getPregnantData(data),
    };

    this.showSpecialAssistanceRequestModal(parts, component, submitEvent);
  }

  getSpecialAssistanceData(data: SupportInformationData) {
    return {
      degreeOfWalking: data.walk,
      blind: data.blind,
      deaf: data.deaf,
      pregnant: data.isPregnant,
    };
  }

  getFoldingTypeData(data: SupportInformationData) {
    return {
      isFolding: data.wheelchairData?.[0]?.isWheelchairFoldable,
      depth: data.wheelchairData?.[0]?.wheelchairDepth ?? 0,
      width: data.wheelchairData?.[0]?.wheelchairWidth ?? 0,
      height: data.wheelchairData?.[0]?.wheelchairHeight ?? 0,
      weight: data.wheelchairData?.[0]?.wheelchairWeight ?? 0,
    };
  }

  getPregnantData(data: SupportInformationData) {
    return {
      doctorCountryPhoneExtension: data.pregnantDoctorCountryPhoneExtension,
      doctorEncryptedPhoneNumber: data.pregnantDoctorEncryptedPhoneNumber,
      doctorPhoneNumber: data.pregnantDoctorPhoneNumber,
      doctorName: data.pregnantDoctorName,
      dueDate: data.pregnantDueDate,
      condition: data.pregnantCondition,
    };
  }

  showSpecialAssistanceRequestModal(
    parts: SupportInformationParts,
    component: Type<SupportModalBlockComponent>,
    submitEvent: (value: SupportInformationData) => void
  ) {
    const modalParts = this._asrModalService.createSpecialAssistanceRequestModalParts(component, parts);
    modalParts.closeEvent = (value?: SpecialAssistanceRequestData) => {
      if (value) {
        const data: SupportInformationData = this.mapSpecialAssistanceRequestToSupportInformationData(parts, value);
        submitEvent(data);
      }
    };

    this._modalService.showSubModal(modalParts);
  }

  mapSpecialAssistanceRequestToSupportInformationData(
    parts: SupportInformationParts,
    value: SpecialAssistanceRequestData
  ): SupportInformationData {
    const walkLevelDisplayValue =
      this._s04CommonSvc.masterData.listDataAll
        .filter((data) => data.data_code === 'PD_006' && data.lang === this._s04CommonSvc.lang)
        .find((data) => data.value === value.walkingAbility.code)?.display_content ?? '';
    const wheelchairBatteryTypeDisplayValue =
      this._s04CommonSvc.masterData.listDataAll
        .filter((data) => data.data_code === 'PD_960' && data.lang === this._s04CommonSvc.lang)
        .find(
          (data) => data.value === this._s04CommonSvc.getWheelchairBatteryDisplayValue(value.wheelchairType.batteryType)
        )?.display_content ?? '';

    const isNeededWheelchair = value.wheelchairs.willBeCheckedIn ?? false;
    const isWheelchairFoldable = value.foldingType.isFolding ?? false;

    return {
      isSupportRegistered: value.isSupportRegistered,
      walk: value.specialAssistance.degreeOfWalking,
      blind: value.specialAssistance.blind,
      deaf: value.specialAssistance.deaf,
      isPregnant: value.specialAssistance.pregnant,
      walkLevel: value.walkingAbility.code,
      walkLevelDisplayValue: walkLevelDisplayValue,
      isNeededWheelchair: isNeededWheelchair,
      isNeededWheelchairDisplayValue: this._s04CommonSvc.getBringWheelchairDisplayValue(isNeededWheelchair),
      isWheelchairFoldable: isWheelchairFoldable,
      isWheelchairFoldableDisplayValue: this._s04CommonSvc.getWheelchairFoldableDisplayValue(isWheelchairFoldable),
      wheelchairType: value.wheelchairType.type,
      wheelchairTypeDisplayValue: this._s04CommonSvc.getWheelchairTypeDisplayValue(value.wheelchairType.type),
      wheelchairBatteryType: value.wheelchairType.batteryType,
      wheelchairBatteryTypeDisplayValue: wheelchairBatteryTypeDisplayValue,
      wheelchairDepth: value.foldingType.depth,
      wheelchairWidth: value.foldingType.width,
      wheelchairHeight: value.foldingType.height,
      wheelchairWeight: value.foldingType.weight,
      isError: false,
      pregnantCondition: value.pregnant.condition,
      pregnantDueDate: value.pregnant.dueDate,
      pregnantDoctorCountryPhoneExtension: value.pregnant.doctorCountryPhoneExtension,
      pregnantDoctorEncryptedPhoneNumber: value.pregnant.doctorEncryptedPhoneNumber,
      pregnantDoctorName: value.pregnant.doctorName,
      pregnantDoctorPhoneNumber: value.pregnant.doctorPhoneNumber,
    };
  }
}
